import { createContext, useEffect } from 'react';
import { MdWarning } from 'react-icons/md';
import {
  ErrorPanel,
  FullScreenOverlay,
  Icon,
  IconContainer,
  LoadingIndicator,
} from '@nbirl/nbi-common-ui-libs';

import { useTableauAccessQuery } from '../../modules/dashboards/queries';
import { Permission } from '../enum';
import { useContextFallback, useErrorToast } from '../hooks';
import { ApiError, CurrentUser } from '../models';
import { useCurrentUserQuery } from '../queries';
import { getPermissions } from '../utils';

type AppCurrentUser = CurrentUser & {
  permissions: Permission[];
  hasTableauAccess: boolean;
};

const CurrentUserContext = createContext<AppCurrentUser | undefined>(undefined);
CurrentUserContext.displayName = 'CurrentUserContext';

const CurrentUserProvider: React.FC = ({ children }) => {
  const currentUserQuery = useCurrentUserQuery();
  const hasTableauAccessQuery = useTableauAccessQuery();
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (hasTableauAccessQuery.error) {
      showErrorToast(hasTableauAccessQuery.error as ApiError);
    }
  }, [hasTableauAccessQuery.error, showErrorToast]);

  if (currentUserQuery.isLoading || hasTableauAccessQuery.isLoading) {
    return (
      <FullScreenOverlay>
        <LoadingIndicator />
      </FullScreenOverlay>
    );
  }

  if (currentUserQuery.isError) {
    return (
      <FullScreenOverlay>
        <ErrorPanel error={currentUserQuery.error} />
      </FullScreenOverlay>
    );
  }

  if (!currentUserQuery.data) {
    return (
      <FullScreenOverlay>
        <div className="flex flex-col items-center space-y-2 h-max">
          <IconContainer variant="light">
            <Icon icon={<MdWarning className="text-primary-500" />} />
          </IconContainer>
          <p className="text-2xl font-medium text-gray-700">
            Unable to fetch the current user profile.
          </p>
          <p>Please contact your administrator.</p>
        </div>
      </FullScreenOverlay>
    );
  }

  return (
    <CurrentUserContext.Provider
      value={{
        ...currentUserQuery.data,
        permissions: getPermissions(currentUserQuery.data.role),
        hasTableauAccess: !!hasTableauAccessQuery.data?.hasTableauAccess,
      }}
    >
      {children}
    </CurrentUserContext.Provider>
  );
};

const useCurrentUser = () => useContextFallback(CurrentUserContext);

export { CurrentUserProvider, useCurrentUser };
